import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import PageStructure from 'flarum/forum/components/PageStructure';
import CommentPost from 'flarum/forum/components/CommentPost';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Button from 'flarum/common/components/Button';
import TopicHeat, { TopicHeatTrend } from './TopicHeat';
import TopicAside from './TopicAside';

/**
 * How the posts on a topic page are ordered.
 *
 * The design has three tabs — newest, hottest, unanswered. The unanswered
 * sort is a client side filter over the discussions: the API already sends
 * `commentCount` with every discussion, so the discussions nobody replied to
 * can be picked out without another request.
 *
 * The names are the sort keys the discussions endpoint understands.
 */
const SORTS = {
  newest: {
    column: '-createdAt',
    label: 'ziiven-topics.forum.page.sort.newest'
  },
  hot: {
    column: '-commentCount',
    label: 'ziiven-topics.forum.page.sort.hot'
  },
  unanswered: {
    column: '-createdAt',
    label: 'ziiven-topics.forum.page.sort.unanswered'
  }
};

/**
 * `/topic/:name` — every post that used this topic.
 *
 * A topic is a word, not a place, so its page is a wall of the posts that
 * used it, gathered from every discussion that mentions it. The posts come
 * from the discussion search (`filter[topic]=…`), which the backend
 * implements, and the discussions themselves decide the order.
 *
 * The page carries the topic's heat (level, score, trend), how much is
 * going on (discussions, posts, last activity) and the hot topics beside it.
 */
export default class TopicPage extends Page {
  /** The topic key from the route — the folded name. */

  /** The topic itself, for the header. `null` once we know there is none. */
  topic = null;

  /** The posts that used the topic, in the order the sort asks for. */
  posts = [];
  loading = true;

  /** The sort key: `newest` or `hot`. */
  sort = 'newest';

  /** Whether the signed-in user follows this topic. */
  followed = false;

  /** Whether the follow request is in flight. */
  followBusy = false;
  oninit(vnode) {
    super.oninit(vnode);
    this.name = (m.route.param('name') || '').toLowerCase();
    app.setTitle(`#${this.name}`);
    this.load();
    this.loadFollowState();
  }

  /**
   * Load the topic, the discussions that use it and their posts.
   */
  async load() {
    this.loading = true;
    await this.loadTopic();
    await this.loadPosts();
    this.loading = false;
    m.redraw();
  }

  /**
   * The topic record.
   *
   * `/api/topics/{key}` resolves a topic by its folded name, but the API
   * does not URL-decode the id, so a non-Latin name never matches. The
   * search filter does decode, so the topic is looked up by name and matched
   * on the folded key.
   */
  async loadTopic() {
    try {
      const found = await app.store.find('topics', {
        filter: {
          q: this.name
        },
        page: {
          limit: 10
        }
      });
      this.topic = found.find(topic => topic.nameKey().toLowerCase() === this.name) ?? found[0] ?? null;
    } catch {
      this.topic = null;
    }
    if (this.topic) {
      app.setTitle(`#${this.topic.name()}`);
    }
  }

  /**
   * The posts.
   *
   * The discussions that use the topic come first, because they decide the
   * order of the wall; then every one of them is asked for its posts.
   */
  async loadPosts() {
    try {
      const response = await app.store.find('discussions', {
        filter: {
          topic: this.name
        },
        sort: SORTS[this.sort].column,
        page: {
          limit: 20
        }
      });

      // "Unanswered" is a filter, not a server sort: the discussions that
      // nobody replied to are the ones with no comments. The attribute comes
      // with every discussion, so this costs nothing.
      const discussions = this.sort === 'unanswered' ? response.filter(discussion => discussion.attribute('commentCount') === 0) : response;
      const perDiscussion = await Promise.all(discussions.map(discussion => app.store.find('posts', {
        filter: {
          discussion: discussion.id()
        },
        include: 'user',
        page: {
          limit: 20
        }
      })));
      this.posts = perDiscussion.reduce((all, posts) => all.concat(posts), []);
    } catch {
      this.posts = [];
    }
  }

  /**
   * Whether the signed-in user follows this topic.
   */
  async loadFollowState() {
    if (!app.session.user) return;
    try {
      const response = await app.request({
        method: 'GET',
        url: `${app.forum.attribute('apiUrl')}/topic-follows/${encodeURIComponent(this.name)}`
      });
      this.followed = Boolean(response?.data?.attributes?.following);
    } catch {
      this.followed = false;
    }
    m.redraw();
  }

  /**
   * Follow or unfollow.
   */
  async toggleFollow() {
    if (!app.session.user || this.followBusy) return;
    this.followBusy = true;
    try {
      await app.request({
        method: this.followed ? 'DELETE' : 'POST',
        url: `${app.forum.attribute('apiUrl')}/topic-follows/${encodeURIComponent(this.name)}`
      });
      this.followed = !this.followed;
    } catch {
      // The request failed; leave the button as it was.
    }
    this.followBusy = false;
    m.redraw();
  }
  view() {
    return m(PageStructure, {
      className: "TopicPage Page--vertical"
    }, this.content());
  }

  /**
   * The column beside the posts.
   *
   * It is part of the page rather than the forum's own sidebar: the theme
   * hides the vendor sidebar on every viewport, so a block put there never
   * reaches the screen. The design's column is this page's anyway.
   */
  content() {
    return m("div", {
      className: "TopicPage-content TopicLayout"
    }, m("div", {
      className: "TopicLayout-main"
    }, this.hero(), this.loading ? m(LoadingIndicator, null) : this.posts.length ? this.wall() : this.empty()), m("aside", {
      className: "TopicLayout-aside"
    }, m(TopicAside, null)));
  }

  /**
   * The topic card: name, heat, trend, the follow button and the numbers.
   */
  hero() {
    const topic = this.topic;
    const display = topic ? topic.name() : this.name;
    return m("div", {
      className: "TopicHero"
    }, m("div", {
      className: "TopicHero-main"
    }, m("h1", {
      className: "TopicHero-title"
    }, m("span", {
      className: "TopicHero-hash"
    }, "#"), display), topic ? m('[', null, m(TopicHeat, {
      level: topic.heatLevel(),
      score: topic.heatScore(),
      badge: true
    }), m(TopicHeatTrend, {
      trend: topic.heatTrend()
    })) : null, this.followButton()), m("div", {
      className: "TopicHero-stats"
    }, m("div", {
      className: "TopicHero-stat"
    }, m("b", null, topic ? Math.round(topic.heatScore()) : 0), m("span", null, app.translator.trans('ziiven-topics.forum.page.stats.heat'))), m("div", {
      className: "TopicHero-stat"
    }, m("b", null, topic ? topic.discussionCount() : 0), m("span", null, app.translator.trans('ziiven-topics.forum.page.stats.discussions'))), m("div", {
      className: "TopicHero-stat"
    }, m("b", null, topic ? topic.postCount() : 0), m("span", null, app.translator.trans('ziiven-topics.forum.page.stats.posts'))), m("div", {
      className: "TopicHero-stat"
    }, m("b", null, topic && topic.lastUsedAt() ? this.relativeDate(topic.lastUsedAt()) : '—'), m("span", null, app.translator.trans('ziiven-topics.forum.page.stats.active')))), m("div", {
      className: "TopicHero-controls"
    }, this.sortControl()));
  }

  /**
   * The follow button. Hidden from guests: there is no account to follow
   * with.
   */
  followButton() {
    if (!app.session.user) return null;
    return m(Button, {
      className: 'Button TopicHero-follow' + (this.followed ? ' TopicHero-follow--on' : ''),
      icon: this.followed ? 'fas fa-check' : 'fas fa-plus',
      loading: this.followBusy,
      onclick: () => this.toggleFollow()
    }, app.translator.trans(this.followed ? 'ziiven-topics.forum.page.following' : 'ziiven-topics.forum.page.follow'));
  }

  /**
   * The sort.
   *
   * A row of tabs, the way the design draws it. Flarum 2.0 has no tab
   * component of its own, so these are core Buttons in a row — the control
   * is a plain button, which is what a tab is.
   */
  sortControl() {
    return m("div", {
      className: "TopicHero-tabs"
    }, Object.keys(SORTS).map(key => m(Button, {
      key: key,
      className: 'Button Button--link TopicHero-tab' + (this.sort === key ? ' active' : ''),
      onclick: () => {
        if (this.sort === key) return;
        this.sort = key;
        this.loadPosts().then(() => m.redraw());
      }
    }, app.translator.trans(SORTS[key].label))));
  }

  /**
   * The wall of posts.
   *
   * The posts are rendered with core's own post component, inside core's
   * post stream markup, so they look exactly like the posts on a discussion
   * page. Core's PostStream is not used here: it is bound to a single
   * discussion, and a topic's posts are spread over as many discussions as
   * people cared to mention it in.
   */
  wall() {
    return m("div", {
      className: "PostStream TopicPage-wall"
    }, this.posts.map(post => m("div", {
      className: "PostStream-item",
      role: "article",
      key: post.id(),
      "data-number": post.number(),
      "data-id": post.id()
    }, m(CommentPost, {
      post: post
    }))));
  }
  empty() {
    return m("p", {
      className: "TopicPage-empty"
    }, app.translator.trans('ziiven-topics.forum.page.empty', {
      name: this.topic ? this.topic.name() : this.name
    }));
  }

  /**
   * "3 hours ago" — when the topic was last used.
   */
  relativeDate(date) {
    return dayjs(date).fromNow();
  }
}
flarum.reg.add('ziiven-topics', 'forum/components/TopicPage', TopicPage);